import { Fragment, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronDown, ChevronLeft, ChevronRight, Megaphone } from 'lucide-react'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Callout } from '@/components/ui/callout'
import { Button } from '@/components/ui/button'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { DetailSkeleton, EmptyState, ErrorState } from '@/components/ui/states'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { requestErrorMessage } from '@/helpers/apiClient'
import { instantLabel } from '@/lib/instantLabel'
import { formatAdDay, formatAdPeriod, formatCount } from '@/lib/adFormat'
import { useOrganization } from '@/hooks/useOrganization'
import { useVenueAds, useVenueAdsPeriod } from '@/hooks/useAds'
import { AdNumberCells, AdNumberHeads } from '@/components/ads/AdNumberCells'
import { comparisonNote, freshnessNote } from '@/components/ads/CompanyAdsTable'
import { RefreshAdsButton } from '@/components/ads/RefreshAdsButton'
import { formatGoogleAdsId } from '@lib/helpers/google-ads-id'
import { useAuth } from '@/auth/AuthContext'
import { admits, atCompanyLevel, can, reachesCompany } from '@/auth/access'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import type { AdContributorDto, VenueAdsDto } from '@lib/dto/ad-numbers.dto'
import type { LeadStatsView } from '@lib/enums/lead-stats-view.enum'
import { SourceTable } from '@/components/pipeline/SourceNumbers'
import { InfoNote, ViewToggle } from '@/components/stats/StatsPieces'

/** Periods shown before "Show earlier periods": about three months. */
const FIRST_PERIODS = 6

const NOT_UPDATING: Record<NonNullable<AdContributorDto['notUpdating']>, { label: string; tone: BadgeTone }> = {
  stopped: { label: 'Stopped', tone: 'neutral' },
  closed: { label: 'Closed in Google', tone: 'neutral' },
  unreachable: { label: 'Can’t be reached', tone: 'danger' },
  signin: { label: 'Sign-in not working', tone: 'danger' },
  no_connection: { label: 'No connection reaches it', tone: 'warning' },
  venue_deleted: { label: 'Venue deleted', tone: 'warning' },
  importing_history: { label: 'Importing history', tone: 'info' },
}

function accountStatus(account: AdContributorDto) {
  if (account.notUpdating === null) {
    return account.importing ? <Badge tone="info">Updating</Badge> : <Badge tone="success">Up to date</Badge>
  }
  const { label, tone } = NOT_UPDATING[account.notUpdating]
  return <Badge tone={tone}>{label}</Badge>
}

function countsHere(account: AdContributorDto): string {
  if (account.isDefault) return 'The whole account'
  return account.campaignNames.length === 1 ? account.campaignNames.join('') : `${String(account.campaignNames.length)} campaigns`
}

/** A quiet label over a group of rows under an open period. */
function GroupLabel({ children }: { children: string }) {
  return (
    <TableRow>
      <TableCell colSpan={8} className="pb-1 pl-10 pt-3 text-xs font-medium text-ink-muted">
        {children}
      </TableCell>
    </TableRow>
  )
}

/** One period's campaigns, under the period's row, fetched when it is opened. */
function PeriodCampaigns({ venueId, start, tracks }: { venueId: string; start: CalendarDay; tracks: VenueAdsDto['tracks'] }) {
  const query = useVenueAdsPeriod(venueId, start)
  if (query.isPending) {
    return (
      <TableRow>
        <TableCell colSpan={8} className="pl-10 text-sm text-ink-muted">
          Loading campaigns…
        </TableCell>
      </TableRow>
    )
  }
  if (query.isError) {
    return (
      <TableRow>
        <TableCell colSpan={8} className="pl-10 text-sm text-danger-ink">
          {requestErrorMessage(query.error)}
        </TableCell>
      </TableRow>
    )
  }
  const trackName = new Map(tracks.map((t) => [t.id, t.name]))
  return (
    <>
      <GroupLabel>By campaign</GroupLabel>
      {query.data.campaigns.map((campaign) => (
        <TableRow key={campaign.campaignId}>
          <TableCell className="min-w-40 pl-10">
            <div className="text-ink">{campaign.name}</div>
            <div className="text-xs text-ink-muted">
              {campaign.accountName}
              {campaign.trackId === null ? '' : ` · ${trackName.get(campaign.trackId) ?? ''}`}
            </div>
          </TableCell>
          <AdNumberCells numbers={campaign.numbers} className="text-control" />
        </TableRow>
      ))}
      {query.data.campaigns.length === 0 ? (
        <TableRow>
          <TableCell colSpan={8} className="pl-10 text-sm text-ink-muted">
            No campaign spent anything or was seen in this period.
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

/** How many of a period's Google Ads inquiries have no campaign known, and why. */
function noCampaignNote(n: number): string {
  const how = n === 1 ? '1 of these Google Ads inquiries has' : `${formatCount(n)} of these Google Ads inquiries have`
  return `${how} no campaign known: their links don’t say which campaign, and Google only says for clicks in the last 90 days.`
}

/**
 * The open period's couples from Google Ads, campaign by campaign
 * (docs/ad-attribution.spec.md): who inquired, toured and booked from each, and
 * what they booked against what it spent, in either of the pipeline's views.
 */
function CampaignCouples({ venueId, start }: { venueId: string; start: CalendarDay }) {
  const [view, setView] = useState<LeadStatsView>('cohort')
  const query = useVenueAdsPeriod(venueId, start, view)
  const attribution = query.data?.attribution ?? null
  if (query.isPending) return <p className="m-0 text-sm text-ink-muted">Counting couples…</p>
  if (query.isError) return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  if (attribution === null) return null
  return (
    <section className="space-y-2">
      <SourceTable
        rows={attribution.rows}
        showsMoney
        groupGoogle={false}
        heading={`Couples by campaign · ${formatAdPeriod(query.data.window.start, query.data.window.end)}`}
        actions={<ViewToggle view={view} onView={setView} />}
      />
      {attribution.noCampaignCount > 0 ? <InfoNote>{noCampaignNote(attribution.noCampaignCount)}</InfoNote> : null}
      {attribution.valuesRecorded ? null : (
        <InfoNote>Booked value isn’t recorded here: this venue doesn’t send its proposals and contracts from FLOH Venues.</InfoNote>
      )}
    </section>
  )
}

/** The venue's periods, newest first; one opens onto its tracks and campaigns. */
function PeriodTable({
  data,
  canRefresh,
}: {
  data: VenueAdsDto
  /** Reading Google again, which is for whoever sets up the company's integrations. */
  canRefresh: boolean
}) {
  const [open, setOpen] = useState<CalendarDay | null>(data.periods[0]?.window.start ?? null)
  const [all, setAll] = useState(false)
  const periods = all ? data.periods : data.periods.slice(0, FIRST_PERIODS)
  const trackName = new Map(data.tracks.map((t) => [t.id, t.name]))
  const newest = data.periods[0]

  return (
    <div className="space-y-2">
      <Table
        heading="Two-week periods"
        actions={canRefresh ? <RefreshAdsButton venueId={data.venueId} busy={data.importing} /> : undefined}
      >
        <TableHeader>
          <TableHead>Period</TableHead>
          <AdNumberHeads />
        </TableHeader>
        <TableBody>
          {periods.map((period) => {
            const isOpen = open === period.window.start
            return (
              <Fragment key={period.window.start}>
                <TableRow selected={isOpen}>
                  <TableCell>
                    <button
                      type="button"
                      className="flex items-start gap-1.5 text-left font-medium text-ink"
                      aria-expanded={isOpen}
                      onClick={() => setOpen(isOpen ? null : period.window.start)}
                    >
                      {isOpen ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />}
                      <span>
                        <span className="whitespace-nowrap">{formatAdPeriod(period.window.start, period.window.end)}</span>
                        {period.window.inProgress ? (
                          <span className="block text-xs font-normal text-ink-muted">
                            In progress · through {formatAdDay(period.window.through)}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </TableCell>
                  <AdNumberCells numbers={period.venue} />
                </TableRow>
                {isOpen && period.tracks.length > 0 ? <GroupLabel>By track</GroupLabel> : null}
                {isOpen
                  ? period.tracks.map((track) => (
                      <TableRow key={track.trackId ?? 'whole'}>
                        <TableCell className="pl-10 text-ink">{track.trackId === null ? 'Whole venue' : trackName.get(track.trackId)}</TableCell>
                        <AdNumberCells numbers={track.numbers} className="text-control" />
                      </TableRow>
                    ))
                  : null}
                {isOpen ? <PeriodCampaigns venueId={data.venueId} start={period.window.start} tracks={data.tracks} /> : null}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
      <p className="m-0 text-xs text-ink-muted">
        {newest === undefined ? '' : comparisonNote(newest.window)} {freshnessNote(data)}
      </p>
      {!all && data.periods.length > FIRST_PERIODS ? (
        <Button variant="ghost" size="sm" onClick={() => setAll(true)}>
          Show earlier periods
        </Button>
      ) : null}
      {open === null ? null : <CampaignCouples venueId={data.venueId} start={open} />}
    </div>
  )
}

/**
 * Accounts behind the numbers that aren't updating, said above them: a short
 * period should not be mistaken for low spend (docs/ad-analytics.spec.md). One
 * still bringing in its history isn't short, only not shown yet.
 */
function ShortNumbers({ accounts }: { accounts: AdContributorDto[] }) {
  const short = accounts.filter((a) => a.notUpdating !== null && a.notUpdating !== 'importing_history')
  if (short.length === 0) return null
  return (
    <Callout tone="attention" title={short.length === 1 ? 'An account here isn’t updating' : 'Some accounts here aren’t updating'}>
      {short
        .map((a) => {
          const reason = a.notUpdating === null ? '' : NOT_UPDATING[a.notUpdating].label.toLowerCase()
          const through = a.importedThrough === null ? '' : `, numbers through ${formatAdDay(a.importedThrough)}`
          return `${a.name}: ${reason}${through}.`
        })
        .join(' ')}
    </Callout>
  )
}

/** The accounts whose numbers count here, and whether each is keeping them current. */
function ContributorTable({ data }: { data: VenueAdsDto }) {
  return (
    <Table heading={data.accounts.length === 1 ? '1 account' : `${String(data.accounts.length)} accounts`}>
      <TableHeader>
        <TableHead>Account</TableHead>
        <TableHead>Counts here</TableHead>
        <TableHead>Status</TableHead>
        <TableHead>Numbers through</TableHead>
      </TableHeader>
      <TableBody>
        {data.accounts.map((account) => (
          <TableRow key={account.accountId}>
            <TableCell className="min-w-48">
              <div className="font-medium text-ink">{account.name}</div>
              <div className="whitespace-nowrap text-xs text-ink-muted tabular-nums">{formatGoogleAdsId(account.externalId)}</div>
            </TableCell>
            <TableCell className="min-w-40">{countsHere(account)}</TableCell>
            <TableCell className="whitespace-nowrap">{accountStatus(account)}</TableCell>
            <TableCell className="whitespace-nowrap">
              {account.importedThrough === null ? '—' : formatAdDay(account.importedThrough)}
              {account.lastUpdatedAt === null ? null : (
                <div className="text-xs text-ink-muted">Updated {instantLabel(account.lastUpdatedAt)}</div>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * A venue's Ads page (docs/ad-analytics.spec.md, "A venue's Ads page"): its
 * periods, newest first, the venue's totals then each track, and any period's
 * campaigns; and the accounts behind them, so a short period is never mistaken
 * for low spend. For staff whose role sees ads; putting accounts in use and
 * reading Google again are for whoever sets up the company's integrations.
 */
export function VenueAdsPage() {
  const { user } = useAuth()
  const { venueId = '' } = useParams()
  const organizationQuery = useOrganization()
  const hasAds = organizationQuery.data?.features.includes('ads') ?? false
  const query = useVenueAds(venueId, hasAds)

  if (organizationQuery.isLoading || (hasAds && query.isPending)) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (!hasAds) {
    return (
      <PageContainer width="wide">
        <EmptyState icon={Megaphone} title="Ads aren’t turned on" description="Ask CSE to turn them on for your company." />
      </PageContainer>
    )
  }
  if (query.isError) {
    return (
      <PageContainer width="wide">
        <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
      </PageContainer>
    )
  }
  const data = query.data
  if (data === undefined) return null
  const setsUpAds = can(user, 'integrations:edit')
  // Accounts are put in use on the company's Ads page, which spans the company.
  const opensCompanyAds = setsUpAds && reachesCompany(user)
  // Reading Google again uses the company's sign-ins, so it is done from across
  // the company, as the API holds it (`requireAdsSetup`).
  const refreshes = setsUpAds && atCompanyLevel(user)

  return (
    <PageContainer width="wide" className="space-y-8">
      <div className="space-y-6">
        {/* The company's Ads table leads here: the way back to it, for whoever can open it. */}
        {atCompanyLevel(user) && admits(user, { staff: 'ads:view', companyWide: true }) ? (
          <Link to="/ads" className="inline-flex items-center gap-1 text-sm font-medium text-ink-muted transition-colors hover:text-ink">
            <ChevronLeft className="h-4 w-4" /> Ads
          </Link>
        ) : null}
        <PageHeader title="Ads" meta={data.venueName} />
      </div>

      {data.accounts.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No Google Ads account counts toward this venue yet"
          description={
            opensCompanyAds
              ? 'Put one in use for it on the company’s Ads page.'
              : 'Whoever sets up the company’s Google Ads can put one in use for it.'
          }
          action={
            opensCompanyAds ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/ads">Open Ads</Link>
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {data.accounts.length > 0 && data.periods.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">
          Bringing in this venue’s history from Google Ads. The numbers appear here when it is in.
        </p>
      ) : null}

      {data.periods.length > 0 ? <ShortNumbers accounts={data.accounts} /> : null}

      {data.periods.length > 0 ? <PeriodTable data={data} canRefresh={refreshes} /> : null}

      {data.accounts.length > 0 ? (
        <section className="space-y-4">
          <SectionHeader title="Accounts" description="The Google Ads accounts whose numbers count toward this venue." />
          <ContributorTable data={data} />
        </section>
      ) : null}
    </PageContainer>
  )
}
